import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './PoolHistory.css';
import './TopupHistory.css'; // import Topup styles to reuse design system

export default function PoolHistory() {
  const navigate = useNavigate();

  const [allDevices, setAllDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [pageMap, setPageMap] = useState({});
  const [hasMore, setHasMore] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [lastSynced, setLastSynced] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);

  const API_BASE =
    process.env.REACT_APP_API_BASE_URL || 'https://viz-sm-be.onrender.com';
  const PAGE_SIZE = 10;

  const getToken = () =>
    localStorage.getItem('authToken') ||
    localStorage.getItem('token') ||
    localStorage.getItem('jwt_token');

  /* ================= DEVICES ================= */
  const fetchUserDevices = async () => {
    try {
      setLoadingDevices(true);
      const token = getToken();
      if (!token) throw new Error('No auth token');

      const res = await axios.get(`${API_BASE}/api/v1/user/home`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const devices = res.data.devices || [];
      setAllDevices(devices);

    if (devices.length > 0) {
    setSelectedDevices(devices.map(d => d.id));  // Select ALL device IDs
    const initialPageMap = {};
    devices.forEach(d => initialPageMap[d.id] = 1);
    setPageMap(initialPageMap);
    }
    } catch {
      setError('Failed to load devices');
    } finally {
      setLoadingDevices(false);
    }
  };

  const formatDateTime = (date) => {
    if (!date) return { date: '-', time: '-' };
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return { date: '-', time: '-' };

    return {
      date: d.toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }),
      time: d.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      }),
    };
  };

  /* ================= HISTORY ================= */
  const fetchPoolHistory = async (loadMore = false) => {
    try {
      loadMore ? setLoadingMore(true) : setLoading(true);
      setError(null);

      if (selectedDevices.length === 0) {
        setTransactions([]);
        setHasMore(false);
        return;
      }

      const token = getToken();
      if (!token) throw new Error('No auth token');

      const requests = selectedDevices.map((deviceId) => {
        const page = pageMap[deviceId] || 1;
        return axios.get(`${API_BASE}/api/v1/user/devicehistory`, {
          params: { deviceId, page, pageSize: PAGE_SIZE },
          headers: { Authorization: `Bearer ${token}` },
        });
      });

      const responses = await Promise.all(requests);

      let allTxns = [];
      let anyHasMore = false;
      const newPageMap = { ...pageMap };

      responses.forEach((res, idx) => {
        const deviceId = selectedDevices[idx];
        const txns = res.data.txns || [];

        allTxns.push(
          ...txns.map((t) => ({
            ...t,
            deviceId,
          }))
        );

        if (res.data.hasMore) {
          anyHasMore = true;
          newPageMap[deviceId] = (newPageMap[deviceId] || 1) + 1;
        }
      });

      allTxns.sort((a, b) => new Date(b.date) - new Date(a.date));

      loadMore
        ? setTransactions((p) => [...p, ...allTxns])
        : setTransactions(allTxns);

      setHasMore(anyHasMore);
      setPageMap(newPageMap);
      setLastSynced(new Date());
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          'Failed to load transfer history'
      );
      if (!loadMore) setTransactions([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchUserDevices();
  }, []);

  useEffect(() => {
    if (!loadingDevices && selectedDevices.length > 0) {
      const map = {};
      selectedDevices.forEach((id) => (map[id] = 1));
      setPageMap(map);
      fetchPoolHistory(false);
    }
  }, [selectedDevices, loadingDevices]);

  useEffect(() => {
  const handleClickOutside = (event) => {
    if (filterOpen && !event.target.closest('.filter-section')) {
      setFilterOpen(false);
    }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [filterOpen]);


  const toggleDevice = (id) => {
    setSelectedDevices((prev) =>
      prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]
    );
  };

  const toggleExpand = (id) => setExpandedId(expandedId === id ? null : id);
  const handleBack = () => navigate('/home');

  /* ================= LOADING DEVICES ================= */
  if (loadingDevices && !allDevices.length) {
    return (
      <div className="topup-history-root">
        <div className="topup-history-header">
          <div className="topup-history-header-side">
            <button className="topup-history-back-btn" onClick={handleBack}>
              ←
            </button>
          </div>
          <div className="topup-history-header-center">
            <h1 className="topup-history-title">Transfer History</h1>
          </div>
          <div className="topup-history-header-side" />
        </div>

        <div className="topup-history-container">
          <div className="topup-history-status-message">Loading devices...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="topup-history-root">
      {/* header (Topup style) */}
      <div className="topup-history-header">
        <div className="topup-history-header-side">
          <button className="topup-history-back-btn" onClick={handleBack}>
            <i className="fas fa-arrow-left"></i>
          </button>
        </div>
        <div className="topup-history-header-center">
          <h1 className="topup-history-title">POOL Transfer History</h1>
        </div>
        <div className="topup-history-header-side" />
      </div>

      <div className="topup-history-container">
        {/* Filter (keeps pool CSS classes) */}
        <div className="filter-section" style={{ width: '40%', display: 'flex', justifyContent: 'center' }}>
          <div style={{ maxWidth: 380, width: '100%' }}>
            <button className="filter-toggle-btn" onClick={() => setFilterOpen(!filterOpen)}>
            <span className="filter-icon">Devices</span>
            <span>({selectedDevices.length})</span>  {/* Dynamic count */}
            <span className={`filter-arrow ${filterOpen ? 'open' : ''}`}>▼</span>
            </button>


            {filterOpen && (
              <div className="filter-dropdown">
                {allDevices.length === 0 ? (
                  <div className="no-devices">No devices linked</div>
                ) : (
                  <div className="device-list">
                    {allDevices.map((d) => (
                      <label key={d.id} className="device-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedDevices.includes(d.id)}
                          onChange={() => toggleDevice(d.id)}
                        />
                        <span className="device-name">{d.id}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Error */}
        {error && <div className="topup-history-error-message">{error}</div>}

        {/* Last synced (Topup sync-row style) */}
        {lastSynced && (
          <div className="topup-history-sync-row">
            <span className="topup-history-sync-label">
              Last updated :   
                {lastSynced.toLocaleTimeString('en-IN', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
              })}
            </span>
          </div>
        )}

        {/* Transactions area (Topup card/list styles applied) */}
        {loading ? (
          <div className="topup-history-status-message">Loading transfers...</div>
        ) : transactions.length === 0 ? (
          <div className="topup-history-empty-card" style={{ maxWidth: 380 }}>
            <div className="empty-title">
              {selectedDevices.length === 0 ? 'Select devices' : 'No transfers'}
            </div>
            <div className="empty-text">Transfers will appear here</div>
          </div>
        ) : (
          <div className="topup-history-list" style={{ width: '90%', maxWidth: 380 }}>
            {transactions.map((txn, idx) => {
              const { date, time } = formatDateTime(txn.date);
              const expanded = expandedId === txn.txnId || expandedId === txn.id;
              const amount = Number(txn.amount || 0);
              const energy = Number(txn.energy || 0);

              return (
                <div
                  key={txn.txnId || txn.id || idx}
                  className="topup-history-card"
                >
                  <div className="topup-history-card-row top" onClick={() => toggleExpand(txn.txnId || txn.id)}>
                    <div className="topup-history-main-id">
                        <span className="value">
                        {date} • {time}
                      </span>
                        <span className="value">
                        DeviceID :
                        <span className="label">  {txn.deviceId || 'Device'}</span>
                        </span> 
                      
                    </div>

                    {/* show amount and status badge if present */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                      <div className="topup-history-detail" style={{ alignItems: 'flex-end' }}>
                        <span className="value amount">₹{amount.toFixed(2)}</span>
                        <span className="value energy">{energy.toFixed(3)} kWh</span>
                      </div>

                      {txn.status && (
                        <span className={`topup-history-status-badge ${txn.status.toLowerCase()}`}>
                          {txn.status.toUpperCase()}
                        </span>
                      )}
                    </div>
                  </div>


                  {expanded && (
                    <div style={{ paddingTop: 8 }}>
                    <div className="topup-history-detail">
                      <span className="label">Transaction ID</span>
                      <span className="value">{txn.txnId || '-'}</span>
                    </div>
                      {txn.summary && (
                        <div className="topup-history-detail">
                          <span className="label">Summary</span>
                          <span className="value">{txn.summary}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Load more */}
        {hasMore && transactions.length > 0 && (
          <div style={{ width: '100%', maxWidth: 380, marginTop: 12 }}>
            <button
              className="topup-history-primary-btn"
              onClick={() => fetchPoolHistory(true)}
              disabled={loadingMore}
              style={{ width: '100%' }}
            >
              {loadingMore ? 'Loading...' : 'Load More Transfers'}
            </button>
          </div>
        )}

        {!hasMore && transactions.length > 0 && (
          <div className="all-loaded" style={{ maxWidth: 380 }}>
             All transfers loaded
          </div>
        )}
      </div>
    </div>
  );
}
